import {
  TERMINOLOGY_CONFIGURATION_CODES,
  TERMINOLOGY_USAGE_KINDS,
  TerminologyUsageKind,
  TerminologyUsageSurface,
  defineTerminologyUsage,
} from "@app/shared/terminology/terminology-usage";
import { TERMINOLOGY_TOKENS, TerminologyTokenCode } from "@app/shared/terminology/terminology-token.catalog";

const fallbacks = {
  incarceratedPersonDisplayName: "Incarcerated Person",
  incarceratedPersonIdentifier: "Incarcerated Person Identifier",
  programScopeKey: "Program Scope",
} as const;

const surface = (
  surfaceCode: string,
  componentCode: string,
  route: string,
): TerminologyUsageSurface => ({
  featureCode: "phms-intake",
  surfaceCode,
  componentCode,
  locations: [{ kind: "route", value: route }],
});

const usage = (
  usageId: string,
  token: (typeof TERMINOLOGY_TOKENS)[keyof typeof TERMINOLOGY_TOKENS],
  fallbackText: string,
  kind: TerminologyUsageKind,
  usageSurface: TerminologyUsageSurface,
  configurationCode: string = TERMINOLOGY_CONFIGURATION_CODES.phmsRuntime,
) =>
  defineTerminologyUsage<TerminologyTokenCode>({
    usageId,
    configurationCode,
    token,
    fallbackText,
    kind,
    surface: usageSurface,
  });

const manualSubmissionSurface = surface(
  "manual-intake-submission",
  "PhmsManualIntakeSubmissionComponent",
  "/phms/intake",
);
const submissionListSurface = surface(
  "intake-submission-list",
  "PhmsIntakeSubmissionListComponent",
  "/phms/intake-submissions",
);
const submissionDetailSurface = surface(
  "intake-submission-detail",
  "PhmsIntakeSubmissionDetailComponent",
  "/phms/intake-submissions",
);
const stagingSurface = surface(
  "intake-staging",
  "PhmsIntakeStagingComponent",
  "/phms/intake-submissions/staging",
);
const reconciliationSurface = surface(
  "intake-reconciliation",
  "PhmsIntakeReconciliationComponent",
  "/phms/intake-submissions/reconciliation",
);

/** Three shared intake tokens and every currently declared client placement of those labels. */
export const PHMS_INTAKE_TERMINOLOGY = {
  manualSubmission: {
    incarceratedPersonDisplayName: usage(
      "phms.intake.manual.display-name.form-label",
      TERMINOLOGY_TOKENS.subjectPerson,
      fallbacks.incarceratedPersonDisplayName,
      TERMINOLOGY_USAGE_KINDS.formLabel,
      manualSubmissionSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    incarceratedPersonIdentifier: usage(
      "phms.intake.manual.identifier.form-label",
      TERMINOLOGY_TOKENS.subjectPersonIdentifier,
      fallbacks.incarceratedPersonIdentifier,
      TERMINOLOGY_USAGE_KINDS.formLabel,
      manualSubmissionSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    programScopeKey: usage(
      "phms.intake.manual.program-scope.form-label",
      TERMINOLOGY_TOKENS.intakeProgramScopeKey,
      fallbacks.programScopeKey,
      TERMINOLOGY_USAGE_KINDS.formLabel,
      manualSubmissionSurface,
    ),
  },
  submissionList: {
    incarceratedPersonIdentifier: usage(
      "phms.intake.submissions.list.identifier.grid-header",
      TERMINOLOGY_TOKENS.subjectPersonIdentifier,
      fallbacks.incarceratedPersonIdentifier,
      TERMINOLOGY_USAGE_KINDS.gridHeader,
      submissionListSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    programScopeKey: usage(
      "phms.intake.submissions.list.program-scope.grid-header",
      TERMINOLOGY_TOKENS.intakeProgramScopeKey,
      fallbacks.programScopeKey,
      TERMINOLOGY_USAGE_KINDS.gridHeader,
      submissionListSurface,
    ),
  },
  submissionDetail: {
    incarceratedPersonDisplayName: usage(
      "phms.intake.submissions.detail.display-name.template-text",
      TERMINOLOGY_TOKENS.subjectPerson,
      fallbacks.incarceratedPersonDisplayName,
      TERMINOLOGY_USAGE_KINDS.templateText,
      submissionDetailSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    incarceratedPersonIdentifier: usage(
      "phms.intake.submissions.detail.identifier.template-text",
      TERMINOLOGY_TOKENS.subjectPersonIdentifier,
      fallbacks.incarceratedPersonIdentifier,
      TERMINOLOGY_USAGE_KINDS.templateText,
      submissionDetailSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    programScopeKey: usage(
      "phms.intake.submissions.detail.program-scope.template-text",
      TERMINOLOGY_TOKENS.intakeProgramScopeKey,
      fallbacks.programScopeKey,
      TERMINOLOGY_USAGE_KINDS.templateText,
      submissionDetailSurface,
    ),
  },
  staging: {
    incarceratedPersonDisplayName: usage(
      "phms.intake.staging.display-name.grid-header",
      TERMINOLOGY_TOKENS.subjectPerson,
      fallbacks.incarceratedPersonDisplayName,
      TERMINOLOGY_USAGE_KINDS.gridHeader,
      stagingSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    incarceratedPersonIdentifier: usage(
      "phms.intake.staging.identifier.grid-header",
      TERMINOLOGY_TOKENS.subjectPersonIdentifier,
      fallbacks.incarceratedPersonIdentifier,
      TERMINOLOGY_USAGE_KINDS.gridHeader,
      stagingSurface,
      TERMINOLOGY_CONFIGURATION_CODES.programPack,
    ),
    programScopeKey: usage(
      "phms.intake.staging.program-scope.grid-header",
      TERMINOLOGY_TOKENS.intakeProgramScopeKey,
      fallbacks.programScopeKey,
      TERMINOLOGY_USAGE_KINDS.gridHeader,
      stagingSurface,
    ),
  },
  reconciliation: {
    programScopeKey: usage(
      "phms.intake.reconciliation.program-scope.form-label",
      TERMINOLOGY_TOKENS.intakeProgramScopeKey,
      fallbacks.programScopeKey,
      TERMINOLOGY_USAGE_KINDS.formLabel,
      reconciliationSurface,
    ),
  },
} as const;

/** Canonical declarations used by the shared runtime label signals. */
export const PHMS_INTAKE_FIELD_USAGES = PHMS_INTAKE_TERMINOLOGY.manualSubmission;
